// src/style.css
var sheet = new CSSStyleSheet();
sheet.replaceSync(`:host {
  --bar-background: var(
    --ts-button-bar-background,
    light-dark(rgb(0 0 0 / 80%), rgb(255 255 255 / 20%))
  );
  --bar-radius: var(--ts-button-bar-border-radius, var(--lozenge-radius));

  --lozenge-background: var(--ts-lozenge-background, rgb(255 255 255 / 20%));
  --lozenge-duration: var(--ts-lozenge-transition-duration, 300ms);
  --lozenge-inset: var(--ts-lozenge-inset, 0 auto 0 0);
  --lozenge-radius: var(--ts-lozenge-border-radius, 999px);
  --lozenge-size: var(
    --ts-lozenge-size,
    calc(var(--button-icon-size) + calc(var(--button-padding) * 2))
  );

  --button-background: var(--ts-button-background, none);
  --button-border: var(--ts-button-border, none);
  --button-color: var(--ts-button-color, white);
  --button-icon-size: var(--ts-button-icon-size, 1.25rem);
  --button-padding: var(--ts-button-padding, 0.5em);

  display: flex;
}

#switch {
  display: flex;
  background: var(--bar-background);
  border-radius: var(--bar-radius);
  font-size: var(--button-icon-size);
  position: relative;

  &::before {
    background: var(--lozenge-background);
    border-radius: var(--lozenge-radius);
    content: ' ';
    display: block;
    height: var(--lozenge-size);
    inset: var(--lozenge-inset);
    position: absolute;
    transition: translate var(--lozenge-duration);
    width: var(--lozenge-size);
  }
}

button {
  align-items: center;
  background: var(--button-background);
  border: var(--button-border);
  color: var(--button-color);
  cursor: pointer;
  display: inline-flex;
  font-size: inherit;
  padding: var(--button-padding);
  position: relative;
  transition-duration: var(--lozenge-duration);
  transition-property: opacity, color;
}

.icon-slot::slotted(*) {
  width: 1em;
  height: 1em;
  pointer-events: none;
}

#dark {
  opacity: 0.5;
}

#light {
  opacity: 1;
}

:host([data-theme='dark']) {
  #switch::before {
    translate: 100%;
  }

  #dark {
    opacity: 1;
  }

  #light {
    opacity: 0.5;
  }
}
`);
var style_default = sheet;

// src/ThemeSwitch.js
var ThemeSwitch = class extends HTMLElement {
  /**
   * Defines the custom element with provided tag name
   */
  static register(tagName = "theme-switch") {
    customElements.define(tagName, this);
  }
  #buttons = [];
  connectedCallback() {
    const {
      darkLabel = "Dark",
      label = "Theme mode",
      lightLabel = "Light"
    } = this.dataset;
    const theme = this.#activeTheme();
    this.dataset.theme = theme;
    const darkPressed = theme === "dark" ? "true" : "false";
    const lightPressed = theme === "light" ? "true" : "false";
    const template = document.createElement("template");
    template.innerHTML = `<slot><div id="switch" part="button-bar" role="group" aria-label="${label}"><button id="light" part="button light-button" type="button" aria-label="${lightLabel}" aria-pressed="${lightPressed}" value="light"><slot name="light-icon" class="icon-slot"><svg aria-hidden="true" part="icon light-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg></slot></button> <button id="dark" part="button dark-button" type="button" aria-label="${darkLabel}" aria-pressed="${darkPressed}" value="dark"><slot name="dark-icon" class="icon-slot"><svg aria-hidden="true" part="icon dark-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg></slot></button></div></slot>`;
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(template.content.cloneNode(true));
    shadow.adoptedStyleSheets.push(style_default);
    const defaultSlot = this.shadowRoot.querySelector("slot");
    this.#buttons = Array.from(defaultSlot.querySelectorAll("button[value]"));
    defaultSlot.addEventListener("slotchange", (ev) => {
      this.#buttons = ev.target.assignedElements().flatMap(
        (el) => el instanceof HTMLButtonElement && el.value !== void 0 ? [el] : Array.from(el.querySelectorAll("button[value]"))
      );
    });
    defaultSlot.addEventListener("click", (ev) => this.#handleThemeSwitch(ev));
    defaultSlot.addEventListener("change", (ev) => this.#handleThemeSwitch(ev));
  }
  disconnectedCallback() {
    this.#buttons.length = 0;
  }
  #activeTheme() {
    let { theme } = this.dataset;
    if (theme) {
      return theme;
    }
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "";
  }
  #handleThemeSwitch(ev) {
    const { strategy, eventName = "themeSwitch" } = this.dataset;
    const button = ev.target instanceof HTMLButtonElement ? ev.target : ev.target.closest("button");
    const theme = ev.target.value ?? button?.value;
    if (theme && theme !== this.dataset.theme && this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        cancelable: true,
        detail: { theme }
      })
    )) {
      if (strategy === "class") {
        if (document.documentElement.classList.contains(this.dataset.theme)) {
          document.documentElement.classList.remove(this.dataset.theme);
        }
        document.documentElement.classList.add(theme);
      } else if (strategy === "attribute") {
        document.documentElement.dataset.theme = theme;
      }
      this.dataset.theme = theme;
      if (this.#buttons.length > 1) {
        this.#buttons.forEach(
          (b) => b.setAttribute("aria-pressed", b.value === theme)
        );
      }
    }
  }
};
export {
  ThemeSwitch as default
};
